import { Anchor, Badge, Box, ScrollArea, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useAuth } from '../../lib/auth-context';
import type { VenueStatus } from '../../lib/venue-status';
import { BrandLogoLink } from '../ui/BrandLogoLink';
import { HatSwitcher } from './HatSwitcher';

/**
 * AppSidebar — fixed-width left nav for every authenticated surface on lg+.
 *
 * Sections:
 *   1. Brand mark + HatSwitcher (only renders for multi-cap users).
 *   2. MAIN  — Dashboard, Inbox (with unread dot when count > 0).
 *   3. DISCOVER — Browse, Events.
 *   4. Hat-conditional section:
 *       - artist → TOURING: Tours, Profile (public preview).
 *       - host   → HOSTING: Venues, Manage events.
 *       - admin  → nothing extra (AdminShell renders the admin sub-nav).
 *
 * The sidebar is rendered by AppShell on lg+; on smaller widths the same
 * items appear inside AppHeader's burger drawer so the navigation model is
 * consistent across breakpoints.
 *
 * Active state is computed from the current router pathname — startsWith
 * checks per item, with an `exact` opt-in for the Dashboard root paths so
 * /admin's sub-routes don't all light up Dashboard.
 *
 * `onNavigate` lets the mobile drawer close itself when a link is tapped.
 */

/** Injected once per mount — adds hover tint to sidebar nav items. */
const SIDEBAR_HOVER_STYLE_ID = 'wwaave-sidebar-hover';
function ensureSidebarHoverStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SIDEBAR_HOVER_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SIDEBAR_HOVER_STYLE_ID;
  style.textContent = `
    .wwaave-sidebar-nav:not([aria-current="page"]):hover {
      background: light-dark(var(--wwaave-brand-hover), var(--wwaave-brand-border)) !important;
    }
  `;
  document.head.appendChild(style);
}

/** Minimal shape of GET /api/venues/me/venues — only what the rail needs. */
type HostVenuesResponse =
  | { ok: true; venues: Array<{ slug: string; status: VenueStatus }> }
  | { ok: false };

export function AppSidebar({
  onNavigate,
  showBrand = true,
}: { onNavigate?: () => void; showBrand?: boolean } = {}) {
  // Inject hover style once on first render.
  ensureSidebarHoverStyle();
  const { user, capabilities, activeHat } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Hat-aware dashboard target. Mirrors UserMenu / AppHeader logic.
  const dashboardTo: '/admin' | '/host/dashboard' | '/dashboard' =
    activeHat === 'admin'
      ? '/admin'
      : activeHat === 'host'
        ? '/host/dashboard'
        : '/dashboard';

  // Inbox unread total. Shares the `['inbox']` query cache with /inbox so
  // when the user is on /inbox we don't re-fetch; on other pages the sidebar
  // primes the cache. Fail-quiet — a network blip never pins a stale red dot.
  const inboxQuery = useQuery({
    queryKey: ['inbox'],
    queryFn: async (): Promise<{ threads: Array<{ unreadCount: number }> }> => {
      const res = await fetch('/api/threads', { credentials: 'include' });
      if (!res.ok) return { threads: [] };
      return res.json();
    },
    enabled: user !== null,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const inboxUnread = (inboxQuery.data?.threads ?? []).reduce(
    (sum, t) => sum + (t.unreadCount > 0 ? 1 : 0),
    0,
  );

  const showTours = activeHat === 'artist' && capabilities?.isArtist === true;
  const showHostNav = activeHat === 'host' && capabilities?.isHost === true;
  const isAdminMode = activeHat === 'admin' && capabilities?.isAdmin === true;

  // Host venues — powers the "Public profile" item in the Hosting section.
  // Shares the `['venues','me']` cache with /host/venues so flipping between
  // the two never re-fetches. Only runs while the host hat is on: artist-hat
  // and logged-out users never issue this request. Fail-quiet like the inbox
  // query above — a blip hides the item rather than rendering a dead link.
  const hostVenuesQuery = useQuery({
    queryKey: ['venues', 'me'],
    queryFn: async (): Promise<HostVenuesResponse> => {
      const res = await fetch('/api/venues/me/venues', { credentials: 'include' });
      if (!res.ok) return { ok: false };
      return res.json();
    },
    enabled: showHostNav,
    staleTime: 60_000,
  });
  // Only `live` venues have a public page — the public venue route serves
  // `status = 'live'` and 404s otherwise, so a draft / in-review / paused
  // venue would be a dead link. Those hosts get no item at all; /host/venues
  // (already in this section) is where they finish and publish.
  const liveVenues =
    hostVenuesQuery.data?.ok === true
      ? hostVenuesQuery.data.venues.filter((v) => v.status === 'live')
      : [];
  const firstLiveVenue = liveVenues[0];

  // The top block carries the brand (mobile drawer) and the HatSwitcher (only
  // shown for multi-hat users). On lg+ the brand lives in the top nav bar, so we
  // drop the block entirely for single-hat users rather than leave an empty band.
  const hatCount = capabilities
    ? [capabilities.isArtist, capabilities.isHost, capabilities.isAdmin].filter(Boolean).length
    : 0;
  const showTopBlock = showBrand || hatCount > 1;

  return (
    <Stack
      gap={0}
      style={{ height: '100%', width: '100%', overflow: 'hidden' }}
    >
      {showTopBlock ? (
        <Box
          style={{
            paddingInline: 'var(--mantine-spacing-md)',
            paddingBlock: 14,
            borderBottom: '1px solid light-dark(var(--wwaave-brand-border), var(--mantine-color-dark-5))',
          }}
        >
          <Stack gap="sm">
            {showBrand ? <BrandLogoLink height={22} /> : null}
            <HatSwitcher fullWidth />
          </Stack>
        </Box>
      ) : null}

      {/* Scrollable nav body */}
      <ScrollArea.Autosize
        type="hover"
        scrollbarSize={6}
        style={{ flex: 1, minHeight: 0 }}
      >
        <Stack
          gap="lg"
          style={{
            paddingInline: 'var(--mantine-spacing-md)',
            paddingBlock: 'var(--mantine-spacing-md)',
          }}
        >
          {isAdminMode ? (
            <AdminSidebarItems pathname={pathname} onNavigate={onNavigate} />
          ) : (
            <>
              <NavSection>
                <NavItem
                  to={dashboardTo}
                  label="Dashboard"
                  icon={<DashboardIcon />}
                  active={isDashboardActive(pathname, dashboardTo)}
                  onNavigate={onNavigate}
                />
                <NavItem
                  to="/inbox"
                  label="Inbox"
                  icon={<InboxIcon />}
                  active={pathname === '/inbox' || pathname.startsWith('/inbox/')}
                  badge={inboxUnread > 0 ? inboxUnread : undefined}
                  onNavigate={onNavigate}
                />
              </NavSection>

              <NavSection label="Discover">
                <NavItem
                  to="/browse"
                  label="Browse venues"
                  icon={<BrowseIcon />}
                  active={pathname === '/browse' || (pathname.startsWith('/browse/') && !pathname.startsWith('/browse/artists'))}
                  onNavigate={onNavigate}
                />
                <NavItem
                  to="/browse/artists"
                  label="Browse artists"
                  icon={<ArtistsIcon />}
                  active={pathname === '/browse/artists' || pathname.startsWith('/browse/artists/')}
                  onNavigate={onNavigate}
                />
                <NavItem
                  to="/events"
                  label="Events"
                  icon={<EventsIcon />}
                  active={pathname === '/events' || pathname.startsWith('/events/')}
                  onNavigate={onNavigate}
                />
              </NavSection>

              {showTours && (
                <NavSection label="Touring">
                  <NavItem
                    to="/tours"
                    label="Tour routes"
                    icon={<RouteIcon />}
                    active={pathname === '/tours' || pathname.startsWith('/tours/')}
                    onNavigate={onNavigate}
                  />
                  <NavItem
                    to="/availability"
                    label="Availability"
                    icon={<CalendarIcon />}
                    active={pathname === '/availability' || pathname.startsWith('/availability/')}
                    onNavigate={onNavigate}
                  />
                  <NavItem
                    to="/profile"
                    label="Public profile"
                    icon={<ProfileIcon />}
                    active={pathname === '/profile' || pathname.startsWith('/profile/')}
                    onNavigate={onNavigate}
                  />
                  <NavItem
                    to="/artist-billing"
                    label="Plan"
                    icon={<PlanIcon />}
                    active={pathname === '/artist-billing' || pathname.startsWith('/artist-billing/')}
                    onNavigate={onNavigate}
                  />
                </NavSection>
              )}

              {showHostNav && (
                <NavSection label="Hosting">
                  <NavItem
                    to="/host/venues"
                    label="Venues"
                    icon={<VenueIcon />}
                    active={pathname.startsWith('/host/venues')}
                    onNavigate={onNavigate}
                  />
                  <NavItem
                    to="/host/events"
                    label="Manage events"
                    icon={<EventsIcon />}
                    active={pathname.startsWith('/host/events')}
                    onNavigate={onNavigate}
                  />
                  <NavItem
                    to="/host/availability"
                    label="Availability"
                    icon={<CalendarIcon />}
                    active={pathname.startsWith('/host/availability')}
                    onNavigate={onNavigate}
                  />
                  {/* One live venue → straight to its public page. Several →
                      /host/venues, which already lists a preview link per
                      venue; a nested sub-list would crowd a 240px rail. */}
                  {firstLiveVenue !== undefined &&
                    (liveVenues.length === 1 ? (
                      <VenueProfileNavItem
                        slug={firstLiveVenue.slug}
                        active={pathname === `/venues/${firstLiveVenue.slug}`}
                        onNavigate={onNavigate}
                      />
                    ) : (
                      <NavItem
                        to="/host/venues"
                        label="Public profile"
                        icon={<ProfileIcon />}
                        active={false}
                        onNavigate={onNavigate}
                      />
                    ))}
                </NavSection>
              )}
            </>
          )}
        </Stack>
      </ScrollArea.Autosize>
    </Stack>
  );
}

/**
 * Dashboard "active" check. Two paths can claim it:
 *   - /dashboard (artist), /host/dashboard (host) — match exact + trailing /.
 *   - /admin (admin) — match exact + trailing /. We deliberately don't claim
 *     /admin/*  because those have their own AdminShell sub-nav.
 */
function isDashboardActive(pathname: string, target: string): boolean {
  if (target === '/admin') {
    return pathname === '/admin' || pathname === '/admin/';
  }
  return pathname === target || pathname === `${target}/`;
}

/**
 * Admin sidebar items. Renders when the active hat is `admin`. Replaces the
 * regular artist/host/discover sections — admin mode is a focused mode, so
 * the surface trims to platform tasks. HatSwitcher (already at the top of
 * AppSidebar) lets the user flip back to artist or host.
 *
 * Active matching uses startsWith so e.g. `/admin/users/abc` keeps "Users"
 * highlighted. Dashboard uses exact match so sub-routes don't all light it.
 */
type AdminLinkItem = {
  label: string;
  to:
    | '/admin'
    | '/admin/launch'
    | '/admin/settings'
    | '/admin/users'
    | '/admin/verifications'
    | '/admin/disputes'
    | '/admin/reports'
    | '/admin/feedback'
    | '/admin/leads'
    | '/admin/exchanges'
    | '/admin/venues'
    | '/admin/subscriptions'
    | '/admin/image-studio'
    | '/admin/analytics'
    | '/admin/analytics-events'
    | '/admin/audit-log'
    | '/admin/sweepers'
    | '/admin/cohort'
    | '/admin/referrals'
    | '/admin/referral-payouts'
    | '/admin/billing-reconciliation'
    | '/admin/announcements';
  exact?: boolean;
};

const ADMIN_LINKS: AdminLinkItem[] = [
  { label: 'Dashboard', to: '/admin', exact: true },
  { label: 'Launch', to: '/admin/launch' },
  { label: 'Leads', to: '/admin/leads' },
  { label: 'Settings', to: '/admin/settings' },
  { label: 'Users', to: '/admin/users' },
  { label: 'Verifications', to: '/admin/verifications' },
  { label: 'Disputes', to: '/admin/disputes' },
  { label: 'Reports', to: '/admin/reports' },
  { label: 'Feedback', to: '/admin/feedback' },
  { label: 'Exchanges', to: '/admin/exchanges' },
  { label: 'Venues', to: '/admin/venues' },
  { label: 'Subscriptions', to: '/admin/subscriptions' },
  { label: 'Image studio', to: '/admin/image-studio' },
  { label: 'Analytics', to: '/admin/analytics' },
  { label: 'Events log', to: '/admin/analytics-events' },
  { label: 'Audit log', to: '/admin/audit-log' },
  { label: 'Sweepers', to: '/admin/sweepers' },
  { label: 'Cohort', to: '/admin/cohort' },
  { label: 'Referrals', to: '/admin/referrals' },
  { label: 'Referral payouts', to: '/admin/referral-payouts' },
  { label: 'Reconciliation', to: '/admin/billing-reconciliation' },
  { label: 'Announcements', to: '/admin/announcements' },
];

function AdminSidebarItems({
  pathname,
  onNavigate,
}: {
  pathname: string;
  onNavigate?: (() => void) | undefined;
}) {
  return (
    <NavSection label="Admin">
      {ADMIN_LINKS.map((link) => {
        const active = link.exact
          ? pathname === link.to || pathname === `${link.to}/`
          : pathname === link.to || pathname.startsWith(`${link.to}/`);
        return (
          <AdminNavItem
            key={link.to}
            to={link.to}
            label={link.label}
            active={active}
            onNavigate={onNavigate}
          />
        );
      })}
    </NavSection>
  );
}

function AdminNavItem({
  to,
  label,
  active,
  onNavigate,
}: {
  to: AdminLinkItem['to'];
  label: string;
  active: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  return (
    <Anchor
      component={Link}
      to={to}
      onClick={onNavigate}
      underline="never"
      className="wwaave-sidebar-nav"
      aria-current={active ? 'page' : undefined}
      style={{ ...navItemStyle(active), display: 'block' }}
    >
      {label}
    </Anchor>
  );
}

/**
 * "Public profile" for a host with exactly one live venue. A separate variant
 * because NavItem's `to` is a flat string-literal union of static paths, while
 * this one needs the dynamic `/venues/$slug` route plus its `params`.
 * (Same reason AdminNavItem exists.)
 */
function VenueProfileNavItem({
  slug,
  active,
  onNavigate,
}: {
  slug: string;
  active: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  return (
    // A typed <Link> rather than Mantine's `<Anchor component={Link}>`: the
    // polymorphic form erases Link's generics to AnyRouter, which rejects
    // `params`. Anchor only contributes underline styling here, and we set
    // underline="never" on the other items anyway, so nothing is lost.
    <Link
      to="/venues/$slug"
      params={{ slug }}
      onClick={onNavigate}
      className="wwaave-sidebar-nav"
      aria-current={active ? 'page' : undefined}
      style={{
        ...navItemStyle(active),
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        textDecoration: 'none',
      }}
    >
      <NavIcon active={active}>
        <ProfileIcon />
      </NavIcon>
      <span style={{ flex: 1, minWidth: 0 }}>Public profile</span>
    </Link>
  );
}

/** Shared visual treatment for every sidebar link (regular, admin, venue). */
function navItemStyle(active: boolean) {
  return {
    padding: '8px 10px',
    borderRadius: 10,
    color: active
      ? 'light-dark(var(--mantine-color-brand-7), var(--mantine-color-brand-4))'
      : 'var(--mantine-color-ink-8)',
    background: active ? 'light-dark(color-mix(in srgb, var(--wwaave-brand-primary) 10%, transparent), var(--wwaave-brand-border))' : 'transparent',
    borderLeft: active ? '4px solid var(--mantine-color-brand-6)' : '4px solid transparent',
    fontWeight: active ? 600 : 500,
    fontSize: 14,
    lineHeight: 1.2,
    transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
  } as const;
}

function NavIcon({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <Box
      component="span"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        color: active ? 'var(--mantine-color-brand-6)' : 'var(--mantine-color-ink-6)',
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
}

function NavSection({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <Stack gap={4}>
      {label ? (
        <Text
          size="xs"
          fw={700}
          tt="uppercase"
          style={{
            letterSpacing: '0.1em',
            fontSize: 11.5,
            paddingInline: 8,
            paddingTop: 4,
            color: 'var(--mantine-color-ink-7)',
          }}
        >
          {label}
        </Text>
      ) : null}
      <Stack gap={2}>{children}</Stack>
    </Stack>
  );
}

function NavItem({
  to,
  label,
  icon,
  active,
  badge,
  onNavigate,
}: {
  to: '/dashboard' | '/host/dashboard' | '/admin' | '/inbox' | '/browse' | '/browse/artists' | '/events' | '/tours' | '/availability' | '/profile' | '/host/venues' | '/host/events' | '/host/availability' | '/artist-billing';
  label: string;
  icon: ReactNode;
  active: boolean;
  badge?: number | undefined;
  onNavigate?: (() => void) | undefined;
}) {
  return (
    <Anchor
      component={Link}
      to={to}
      onClick={onNavigate}
      underline="never"
      className="wwaave-sidebar-nav"
      aria-current={active ? 'page' : undefined}
      style={{ ...navItemStyle(active), display: 'flex', alignItems: 'center', gap: 10 }}
    >
      <NavIcon active={active}>{icon}</NavIcon>
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      {badge !== undefined ? (
        <Badge
          size="sm"
          variant="filled"
          color="brand.6"
          radius="xl"
          aria-label={`${badge} unread`}
          style={{ flexShrink: 0, minWidth: 20, height: 20, paddingInline: 6 }}
        >
          {badge > 99 ? '99+' : badge}
        </Badge>
      ) : null}
    </Anchor>
  );
}

// ─── icons ────────────────────────────────────────────────────────────────
// Inline SVGs to avoid pulling in a full icon library for ~6 glyphs. Stroke
// rides currentColor so each icon picks up its NavItem's active-state colour.

const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  style: { display: 'block' as const },
};

function DashboardIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 12 12 4l9 8" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

function BrowseIcon() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function EventsIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18" />
    </svg>
  );
}

function RouteIcon() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="6" cy="19" r="3" />
      <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
      <circle cx="18" cy="5" r="3" />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
    </svg>
  );
}

function VenueIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 21h18" />
      <path d="M5 21V8l7-5 7 5v13" />
      <path d="M10 21v-6h4v6" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4" />
      <path d="M8 2v4" />
      <path d="M3 10h18" />
      <path d="M8 14h.01" />
      <path d="M12 14h.01" />
      <path d="M16 14h.01" />
    </svg>
  );
}

function ArtistsIcon() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="9" cy="7" r="4" />
      <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      <path d="M21 21v-2a4 4 0 0 0-3-3.85" />
    </svg>
  );
}

function PlanIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M2 17 12 22l10-5" />
      <path d="M2 12 12 17l10-5" />
      <path d="M12 2 2 7l10 5 10-5-10-5z" />
    </svg>
  );
}
